import type { DueEntryT, StatefulTrackerViewT } from "@patient-watch/types";
import dayjs from "dayjs";

import { isActionableDueEntry } from "./findEarliestDueEntry";

type ScheduleEntry = NonNullable<DueEntryT>;

export { isActionableDueEntry };

export function getTrackerSortOrder(tracker: StatefulTrackerViewT): number {
    return tracker.sort_order ?? 0;
}

export function compareTrackersBySortOrder(a: StatefulTrackerViewT, b: StatefulTrackerViewT): number {
    const orderDiff = getTrackerSortOrder(a) - getTrackerSortOrder(b);
    if (orderDiff !== 0) return orderDiff;
    return String(a.created_at ?? "").localeCompare(String(b.created_at ?? ""));
}

export function hasActionableDueEntry(tracker: StatefulTrackerViewT): boolean {
    return (tracker.due_entries ?? []).some(isActionableDueEntry);
}

export function hasSequenceBlockedDueEntry(tracker: StatefulTrackerViewT, now: dayjs.Dayjs = dayjs()): boolean {
    return (tracker.upcoming_entries ?? []).some((entry) => {
        if (entry == null || entry.lock_reason !== "sequence") return false;
        if (!entry.due_at) return true;
        return !dayjs(entry.due_at).isAfter(now);
    });
}

export function getSequenceBlockedEntry(tracker: StatefulTrackerViewT): ScheduleEntry | null {
    const now = dayjs();
    const blocked = (tracker.upcoming_entries ?? []).find(
        (entry) =>
            entry != null && entry.lock_reason === "sequence" && entry.due_at && !dayjs(entry.due_at).isAfter(now)
    );
    return blocked ?? null;
}

export function getSequenceBlockedDueEntries(
    tracker: StatefulTrackerViewT,
    now: dayjs.Dayjs = dayjs()
): ScheduleEntry[] {
    return (tracker.upcoming_entries ?? []).filter((entry): entry is ScheduleEntry => {
        if (entry == null || entry.lock_reason !== "sequence") return false;
        if (!entry.due_at) return true;
        return !dayjs(entry.due_at).isAfter(now);
    });
}

export function getSequenceBlockedReason(entry: ScheduleEntry | null | undefined): string | null {
    if (!entry?.blocked_by_form_name) return null;
    return [entry.blocked_by_form_name, entry.blocked_by_timing_label].filter(Boolean).join(" ");
}

/** Redirect form-fill routes when the tracker is blocked by sequential gating. */
export function isTrackerBlockedBySequence(
    tracker: StatefulTrackerViewT | null | undefined,
    isUnscheduled = false
): boolean {
    if (!tracker || isUnscheduled) {
        return false;
    }
    return !hasActionableDueEntry(tracker) && hasSequenceBlockedDueEntry(tracker);
}
